import { z } from 'zod';
import type { BackupData, BackupEnvelope, BackupMetadata, TaskOccurrence } from '@/types';
import { isLocalDateKey, isTimeOfDay } from './dates';
import { createId } from './ids';

export const BACKUP_FORMAT = 'discipline-backup' as const;
export const CURRENT_BACKUP_SCHEMA_VERSION = 2;
const BACKUP_DATA_KEYS = [
  'userSettings',
  'appPreferences',
  'projects',
  'projectMilestones',
  'taskTemplates',
  'taskOccurrences',
  'taskProgressEntries',
  'focusSessions',
  'dayRecords',
  'dayReflections',
  'habits',
  'habitCompletions',
  'streakRecords',
  'xpEvents',
  'auditEvents',
  'schoolSubjects',
  'studyTopics',
  'studySessions',
  'frenchSessions',
  'workoutSessions',
  'exerciseSets',
  'novelProjects',
  'novelChapters',
  'youtubeVideos',
  'sleepRecords',
  'backupMetadata'
] as const satisfies readonly (keyof BackupData)[];

const isoDateTimeSchema = z.string().datetime({ offset: true });
const localDateSchema = z.string().refine(isLocalDateKey, 'Expected a valid YYYY-MM-DD local date');
const timeSchema = z.string().refine(isTimeOfDay, 'Expected a valid HH:mm local time');
const nonNegativeNumber = z.number().finite().nonnegative();
const entityFields = {
  id: z.string().min(1),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
};
const appAreaSchema = z.enum([
  'school',
  'french',
  'sport',
  'youtube',
  'novel',
  'sleep',
  'personal'
]);
const taskCategorySchema = z.enum([
  'school',
  'french',
  'sport',
  'youtube',
  'novel',
  'sleep',
  'personal',
  'routine',
  'break',
  'planning',
  'general'
]);
const taskTypeSchema = z.enum([
  'task',
  'checkbox',
  'timed',
  'countable',
  'habit',
  'study',
  'workout',
  'chapter',
  'project',
  'break',
  'sleep'
]);
const prioritySchema = z.enum(['low', 'normal', 'high', 'mandatory']);
const taskStatusSchema = z.enum([
  'planned',
  'active',
  'paused',
  'completed',
  'missed',
  'skipped',
  'deferred'
]);
const strictModeSchema = z.enum(['off', 'strict', 'very-strict']);
const weekdaySchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
]);
const recurrenceEndFields = { endDate: localDateSchema.optional(), paused: z.boolean() };

export const recurrenceRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('once'), date: localDateSchema, ...recurrenceEndFields }).strict(),
  z
    .object({
      kind: z.literal('daily'),
      startDate: localDateSchema,
      intervalDays: z.number().int().positive(),
      ...recurrenceEndFields
    })
    .strict(),
  z
    .object({
      kind: z.literal('weekdays'),
      startDate: localDateSchema,
      weekdays: z.array(weekdaySchema).min(1),
      intervalWeeks: z.number().int().positive(),
      ...recurrenceEndFields
    })
    .strict(),
  z
    .object({
      kind: z.literal('weekly'),
      startDate: localDateSchema,
      weekday: weekdaySchema,
      intervalWeeks: z.number().int().positive(),
      ...recurrenceEndFields
    })
    .strict(),
  z
    .object({
      kind: z.literal('monthly'),
      startDate: localDateSchema,
      dayOfMonth: z.number().int().min(1).max(31),
      intervalMonths: z.number().int().positive(),
      overflow: z.enum(['skip', 'last-day']),
      ...recurrenceEndFields
    })
    .strict(),
  z
    .object({
      kind: z.literal('custom'),
      startDate: localDateSchema,
      interval: z.number().int().positive(),
      unit: z.enum(['days', 'weeks', 'months']),
      weekdays: z.array(weekdaySchema).min(1).optional(),
      dayOfMonth: z.number().int().min(1).max(31).optional(),
      overflow: z.enum(['skip', 'last-day']).optional(),
      ...recurrenceEndFields
    })
    .strict()
]);

const userSettingsSchema = z
  .object({
    ...entityFields,
    name: z.string().min(1),
    primaryGoal: z.string(),
    wakeTime: timeSchema,
    bedTime: timeSchema,
    activeAreas: z.array(appAreaSchema),
    dailyGoals: z
      .object({
        schoolMinutes: nonNegativeNumber,
        frenchMinutes: nonNegativeNumber,
        youtubeMinutes: nonNegativeNumber,
        novelChapters: nonNegativeNumber,
        pushUps: nonNegativeNumber,
        squats: nonNegativeNumber
      })
      .strict(),
    currentPushUpMaximum: nonNegativeNumber,
    longTermPushUpGoal: nonNegativeNumber,
    onboardingCompleted: z.boolean(),
    onboardingCompletedAt: isoDateTimeSchema.optional()
  })
  .strict();

const appPreferencesSchema = z
  .object({
    ...entityFields,
    theme: z.enum(['dark', 'light', 'system']),
    locale: z.enum(['de-CH', 'de-DE', 'en']),
    reducedMotion: z.boolean(),
    compactMode: z.boolean(),
    notificationPermission: z.enum(['not-asked', 'unsupported', 'granted', 'denied', 'later']),
    lastOpenedDate: localDateSchema.optional(),
    lastBackupAt: isoDateTimeSchema.optional(),
    hasSeenInstallHelp: z.boolean(),
    hasSeenNotificationExplanation: z.boolean()
  })
  .strict();

const projectSchema = z
  .object({
    ...entityFields,
    title: z.string().min(1),
    description: z.string(),
    goal: z.string(),
    area: z.union([appAreaSchema, z.literal('general')]),
    startDate: localDateSchema,
    targetDate: localDateSchema.optional(),
    status: z.enum(['active', 'paused', 'completed', 'archived']),
    progress: nonNegativeNumber,
    progressUnit: z.string(),
    color: z.string().optional(),
    notes: z.string(),
    archivedAt: isoDateTimeSchema.optional()
  })
  .strict();

const projectMilestoneSchema = z
  .object({
    ...entityFields,
    projectId: z.string().min(1),
    title: z.string().min(1),
    description: z.string(),
    targetValue: nonNegativeNumber.optional(),
    currentValue: nonNegativeNumber.optional(),
    targetDate: localDateSchema.optional(),
    completedAt: isoDateTimeSchema.optional(),
    sortOrder: z.number().int()
  })
  .strict();

export const taskTemplateSchema = z
  .object({
    ...entityFields,
    title: z.string().min(1),
    description: z.string(),
    taskType: taskTypeSchema,
    category: taskCategorySchema,
    priority: prioritySchema,
    recurrence: recurrenceRuleSchema,
    defaultStartTime: timeSchema.optional(),
    defaultEndTime: timeSchema.optional(),
    estimatedMinutes: nonNegativeNumber.optional(),
    defaultTargetValue: nonNegativeNumber.optional(),
    unit: z.string().optional(),
    strictMode: strictModeSchema,
    projectId: z.string().optional(),
    active: z.boolean(),
    effectiveFrom: localDateSchema,
    effectiveUntil: localDateSchema.optional(),
    revision: z.number().int().positive(),
    sortOrder: z.number().int(),
    notes: z.string(),
    tags: z.array(z.string())
  })
  .strict();

export const taskOccurrenceSchema = z
  .object({
    ...entityFields,
    occurrenceKey: z.string().min(1),
    templateId: z.string().optional(),
    templateRevision: z.number().int().positive().optional(),
    title: z.string().min(1),
    description: z.string(),
    taskType: taskTypeSchema,
    category: taskCategorySchema,
    priority: prioritySchema,
    status: taskStatusSchema,
    date: localDateSchema,
    startTime: timeSchema.optional(),
    endTime: timeSchema.optional(),
    estimatedMinutes: nonNegativeNumber.optional(),
    targetValue: nonNegativeNumber.optional(),
    currentValue: nonNegativeNumber,
    unit: z.string().optional(),
    strictMode: strictModeSchema,
    projectId: z.string().optional(),
    notes: z.string(),
    sortOrder: z.number().int(),
    startedAt: isoDateTimeSchema.optional(),
    completedAt: isoDateTimeSchema.optional(),
    skippedAt: isoDateTimeSchema.optional(),
    skipReason: z.string().optional(),
    deferredFromDate: localDateSchema.optional(),
    deferredToDate: localDateSchema.optional(),
    sourceOccurrenceId: z.string().optional()
  })
  .strict();

const taskProgressEntrySchema = z
  .object({
    ...entityFields,
    occurrenceId: z.string(),
    date: localDateSchema,
    kind: z.enum(['increment', 'correction', 'manual-time', 'focus-credit']),
    delta: z.number().finite(),
    resultingValue: nonNegativeNumber,
    unit: z.string().optional(),
    note: z.string().optional(),
    focusSessionId: z.string().optional()
  })
  .strict();

export const focusSessionSchema = z
  .object({
    ...entityFields,
    occurrenceId: z.string(),
    taskTemplateId: z.string().optional(),
    date: localDateSchema,
    status: z.enum(['running', 'paused', 'completed', 'cancelled']),
    plannedDurationSeconds: z.number().int().positive(),
    startedAt: isoDateTimeSchema,
    plannedEndAt: isoDateTimeSchema,
    lastResumedAt: isoDateTimeSchema,
    pausedAt: isoDateTimeSchema.optional(),
    completedAt: isoDateTimeSchema.optional(),
    accumulatedSeconds: nonNegativeNumber,
    creditedMinutes: z.number().int().nonnegative(),
    note: z.string().optional()
  })
  .strict();

export const dayRecordSchema = z
  .object({
    ...entityFields,
    date: localDateSchema,
    isClosed: z.boolean(),
    closedAt: isoDateTimeSchema.optional(),
    plannedTasks: z.number().int().nonnegative(),
    completedTasks: z.number().int().nonnegative(),
    missedTasks: z.number().int().nonnegative(),
    mandatoryTasks: z.number().int().nonnegative(),
    completedMandatoryTasks: z.number().int().nonnegative(),
    focusMinutes: nonNegativeNumber,
    schoolMinutes: nonNegativeNumber,
    frenchMinutes: nonNegativeNumber,
    youtubeMinutes: nonNegativeNumber,
    novelChapters: nonNegativeNumber,
    pushUps: nonNegativeNumber,
    squats: nonNegativeNumber,
    earnedXp: z.number().finite(),
    completionRate: z.number().min(0).max(100)
  })
  .strict();

const dayReflectionSchema = z
  .object({
    ...entityFields,
    date: localDateSchema,
    wentWell: z.string(),
    blockers: z.string(),
    improvement: z.string(),
    mostImportantTomorrow: z.string()
  })
  .strict();

const habitSchema = z
  .object({
    ...entityFields,
    taskTemplateId: z.string(),
    title: z.string(),
    category: taskCategorySchema,
    recurrence: recurrenceRuleSchema,
    targetValue: nonNegativeNumber,
    unit: z.string(),
    active: z.boolean(),
    startedOn: localDateSchema,
    archivedAt: isoDateTimeSchema.optional()
  })
  .strict();

const habitCompletionSchema = z
  .object({
    ...entityFields,
    completionKey: z.string(),
    habitId: z.string(),
    occurrenceId: z.string().optional(),
    date: localDateSchema,
    status: z.enum(['completed', 'missed', 'excused']),
    value: nonNegativeNumber,
    completedAt: isoDateTimeSchema.optional(),
    note: z.string().optional()
  })
  .strict();

const streakRecordSchema = z
  .object({
    ...entityFields,
    habitId: z.string(),
    calculatedThrough: localDateSchema,
    currentStreak: z.number().int().nonnegative(),
    longestStreak: z.number().int().nonnegative(),
    weeklyRate: z.number().min(0).max(100),
    monthlyRate: z.number().min(0).max(100),
    lastCompletedDate: localDateSchema.optional()
  })
  .strict();

const xpEventSchema = z
  .object({
    ...entityFields,
    sourceKey: z.string(),
    sourceType: z.enum([
      'task',
      'focus',
      'mandatory-task',
      'school-goal',
      'french-goal',
      'sport-goal',
      'novel-goal',
      'youtube-goal',
      'complete-day',
      'correction'
    ]),
    sourceId: z.string(),
    date: localDateSchema,
    amount: z.number().finite(),
    reason: z.string(),
    correctedAt: isoDateTimeSchema.optional()
  })
  .strict();

const auditEventSchema = z
  .object({
    ...entityFields,
    entityType: z.string(),
    entityId: z.string(),
    action: z.enum([
      'create',
      'update',
      'delete-attempt',
      'target-change',
      'complete',
      'skip',
      'defer',
      'import',
      'reset'
    ]),
    date: localDateSchema,
    reason: z.string().optional(),
    before: z.record(z.string(), z.unknown()).optional(),
    after: z.record(z.string(), z.unknown()).optional(),
    strictMode: strictModeSchema.optional()
  })
  .strict();

function relatedEntitySchema(fields: z.ZodRawShape): z.ZodObject<z.ZodRawShape> {
  return z.object({ ...entityFields, ...fields }).passthrough();
}

const schoolSubjectSchema = relatedEntitySchema({ projectId: z.string(), name: z.string() });
const studyTopicSchema = relatedEntitySchema({
  subjectId: z.string(),
  title: z.string(),
  understanding: z.number().int().min(1).max(5)
});
const studySessionSchema = relatedEntitySchema({
  subjectId: z.string(),
  date: localDateSchema,
  durationMinutes: nonNegativeNumber
});
const frenchSessionSchema = relatedEntitySchema({
  date: localDateSchema,
  area: z.enum(['vocabulary', 'grammar', 'listening', 'reading', 'speaking']),
  durationMinutes: nonNegativeNumber
});
const workoutSessionSchema = relatedEntitySchema({
  date: localDateSchema,
  durationMinutes: nonNegativeNumber
});
const exerciseSetSchema = relatedEntitySchema({
  workoutSessionId: z.string(),
  exercise: z.string(),
  setNumber: z.number().int().positive()
});
const novelProjectSchema = relatedEntitySchema({ projectId: z.string(), title: z.string() });
const novelChapterSchema = relatedEntitySchema({
  novelProjectId: z.string(),
  chapterNumber: z.number().int().positive(),
  status: z.enum(['idea', 'planned', 'prompt-ready', 'draft', 'reviewed', 'finished', 'uploaded'])
});
const youtubeVideoSchema = relatedEntitySchema({
  projectId: z.string(),
  title: z.string(),
  status: z.enum([
    'idea',
    'research',
    'script',
    'production',
    'editing',
    'thumbnail',
    'ready',
    'published'
  ])
});
const sleepRecordSchema = relatedEntitySchema({
  date: localDateSchema,
  plannedBedTime: timeSchema,
  plannedWakeTime: timeSchema
});

export const backupMetadataSchema = z
  .object({
    ...entityFields,
    backupId: z.string(),
    schemaVersion: z.number().int().positive(),
    appVersion: z.string(),
    exportedAt: isoDateTimeSchema,
    deviceLabel: z.string().optional(),
    recordCounts: z.record(z.string(), z.number().int().nonnegative()),
    checksum: z.string().optional()
  })
  .strict();

export const backupDataSchema = z
  .object({
    userSettings: z.array(userSettingsSchema),
    appPreferences: z.array(appPreferencesSchema),
    projects: z.array(projectSchema),
    projectMilestones: z.array(projectMilestoneSchema),
    taskTemplates: z.array(taskTemplateSchema),
    taskOccurrences: z.array(taskOccurrenceSchema),
    taskProgressEntries: z.array(taskProgressEntrySchema),
    focusSessions: z.array(focusSessionSchema),
    dayRecords: z.array(dayRecordSchema),
    dayReflections: z.array(dayReflectionSchema),
    habits: z.array(habitSchema),
    habitCompletions: z.array(habitCompletionSchema),
    streakRecords: z.array(streakRecordSchema),
    xpEvents: z.array(xpEventSchema),
    auditEvents: z.array(auditEventSchema),
    schoolSubjects: z.array(schoolSubjectSchema),
    studyTopics: z.array(studyTopicSchema),
    studySessions: z.array(studySessionSchema),
    frenchSessions: z.array(frenchSessionSchema),
    workoutSessions: z.array(workoutSessionSchema),
    exerciseSets: z.array(exerciseSetSchema),
    novelProjects: z.array(novelProjectSchema),
    novelChapters: z.array(novelChapterSchema),
    youtubeVideos: z.array(youtubeVideoSchema),
    sleepRecords: z.array(sleepRecordSchema),
    backupMetadata: z.array(backupMetadataSchema)
  })
  .strict();

export const backupEnvelopeSchema = z
  .object({
    format: z.literal(BACKUP_FORMAT),
    schemaVersion: z.literal(CURRENT_BACKUP_SCHEMA_VERSION),
    appVersion: z.string().min(1),
    exportedAt: isoDateTimeSchema,
    metadata: backupMetadataSchema,
    data: backupDataSchema
  })
  .strict();

const legacyTaskOccurrenceSchema = taskOccurrenceSchema
  .omit({ occurrenceKey: true })
  .extend({ occurrenceKey: z.string().min(1).optional() });
const legacyBackupDataSchema = backupDataSchema.partial().extend({
  taskOccurrences: z.array(legacyTaskOccurrenceSchema).optional()
});
const legacyBackupSchema = z
  .object({
    format: z.literal(BACKUP_FORMAT).optional(),
    schemaVersion: z.literal(1),
    appVersion: z.string().default('legacy'),
    exportedAt: isoDateTimeSchema,
    metadata: backupMetadataSchema.optional(),
    data: legacyBackupDataSchema
  })
  .strict();

export type BackupValidationResult =
  | { success: true; backup: BackupEnvelope; migratedFrom?: number }
  | { success: false; errors: string[] };

export function emptyBackupData(): BackupData {
  return {
    userSettings: [],
    appPreferences: [],
    projects: [],
    projectMilestones: [],
    taskTemplates: [],
    taskOccurrences: [],
    taskProgressEntries: [],
    focusSessions: [],
    dayRecords: [],
    dayReflections: [],
    habits: [],
    habitCompletions: [],
    streakRecords: [],
    xpEvents: [],
    auditEvents: [],
    schoolSubjects: [],
    studyTopics: [],
    studySessions: [],
    frenchSessions: [],
    workoutSessions: [],
    exerciseSets: [],
    novelProjects: [],
    novelChapters: [],
    youtubeVideos: [],
    sleepRecords: [],
    backupMetadata: []
  };
}

export function countBackupRecords(data: BackupData): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const name of BACKUP_DATA_KEYS) counts[name] = data[name].length;
  return counts;
}

export function buildBackupMetadata(
  data: BackupData,
  appVersion: string,
  exportedAt = new Date().toISOString()
): BackupMetadata {
  const id = createId();
  return {
    id,
    backupId: id,
    schemaVersion: CURRENT_BACKUP_SCHEMA_VERSION,
    appVersion,
    exportedAt,
    recordCounts: countBackupRecords(data),
    createdAt: exportedAt,
    updatedAt: exportedAt
  };
}

export function createBackupEnvelope(
  data: BackupData,
  appVersion: string,
  exportedAt = new Date().toISOString()
): BackupEnvelope {
  const metadata = buildBackupMetadata(data, appVersion, exportedAt);
  return {
    format: BACKUP_FORMAT,
    schemaVersion: CURRENT_BACKUP_SCHEMA_VERSION,
    appVersion,
    exportedAt,
    metadata,
    data
  };
}

function zodErrors(error: z.ZodError): string[] {
  return error.issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : 'backup';
    return `${path}: ${issue.message}`;
  });
}

function migrateVersion1(input: z.infer<typeof legacyBackupSchema>): unknown {
  const defaults = emptyBackupData();
  const occurrences = (input.data.taskOccurrences ?? []).map((occurrence) => ({
    ...occurrence,
    occurrenceKey:
      occurrence.occurrenceKey ??
      (occurrence.templateId
        ? `${occurrence.templateId}:${occurrence.date}`
        : `legacy:${occurrence.id}:${occurrence.date}`)
  })) as TaskOccurrence[];
  const data = {
    ...defaults,
    ...input.data,
    taskOccurrences: occurrences
  } as BackupData;
  const metadata = buildBackupMetadata(data, input.appVersion, input.exportedAt);
  return {
    format: BACKUP_FORMAT,
    schemaVersion: CURRENT_BACKUP_SCHEMA_VERSION,
    appVersion: input.appVersion,
    exportedAt: input.exportedAt,
    metadata,
    data
  };
}

export function validateAndMigrateBackup(input: unknown): BackupValidationResult {
  const header = z.object({ schemaVersion: z.number().int() }).passthrough().safeParse(input);
  if (!header.success) return { success: false, errors: zodErrors(header.error) };

  let candidate: unknown = input;
  let migratedFrom: number | undefined;
  if (header.data.schemaVersion === 1) {
    const legacy = legacyBackupSchema.safeParse(input);
    if (!legacy.success) return { success: false, errors: zodErrors(legacy.error) };
    candidate = migrateVersion1(legacy.data);
    migratedFrom = 1;
  } else if (header.data.schemaVersion !== CURRENT_BACKUP_SCHEMA_VERSION) {
    return {
      success: false,
      errors: [
        header.data.schemaVersion > CURRENT_BACKUP_SCHEMA_VERSION
          ? `Backup schema ${header.data.schemaVersion} is newer than supported schema ${CURRENT_BACKUP_SCHEMA_VERSION}.`
          : `Backup schema ${header.data.schemaVersion} is not supported.`
      ]
    };
  }

  const parsed = backupEnvelopeSchema.safeParse(candidate);
  if (!parsed.success) return { success: false, errors: zodErrors(parsed.error) };
  return {
    success: true,
    backup: parsed.data as unknown as BackupEnvelope,
    ...(migratedFrom ? { migratedFrom } : {})
  };
}

export function parseBackup(input: unknown): BackupEnvelope {
  const result = validateAndMigrateBackup(input);
  if (!result.success) throw new Error(`Ungültiges Backup: ${result.errors.join(' ')}`);
  return result.backup;
}

export interface BackupPreview {
  schemaVersion: number;
  appVersion: string;
  exportedAt: string;
  recordCounts: Record<string, number>;
  migratedFrom?: number;
}

export function previewBackup(input: unknown): BackupPreview {
  const result = validateAndMigrateBackup(input);
  if (!result.success) throw new Error(`Ungültiges Backup: ${result.errors.join(' ')}`);
  return {
    schemaVersion: result.backup.schemaVersion,
    appVersion: result.backup.appVersion,
    exportedAt: result.backup.exportedAt,
    recordCounts: countBackupRecords(result.backup.data),
    ...(result.migratedFrom ? { migratedFrom: result.migratedFrom } : {})
  };
}
